import { SearchProductOutputItem } from '@/lib/api/products'
import type { SelectionStep } from './types'

export type ResourceIdKind = 'product' | 'variant' | 'price'

export interface ResourceIdQuery {
    kind: ResourceIdKind
    id: string
}

// Tagada resource ids are `<kind>_<opaque suffix>`.
const RESOURCE_ID_PATTERN = /^(product|variant|price)_[A-Za-z0-9_-]+$/

/**
 * Read a pasted product / variant / price id out of the search box. Anything
 * else is a plain name search and returns null.
 */
export const parseResourceId = (value: string): ResourceIdQuery | null => {
    const trimmed = value.trim()
    const match = RESOURCE_ID_PATTERN.exec(trimmed)
    if (!match) return null

    return { kind: match[1] as ResourceIdKind, id: trimmed }
}

export interface ResolvedSelection {
    product: SearchProductOutputItem
    variantId?: string
    priceId?: string
}

interface ResolveIdSelectionArgs {
    query: ResourceIdQuery
    product: SearchProductOutputItem | null | undefined
    storeId: string
    includeInactive: boolean
}

/**
 * Turn a pasted id, plus the product it resolved to, into the deepest selection
 * that id makes unambiguous — a price id fills all three steps, a variant id
 * fills two. Remaining steps that only have one option are collapsed too, so a
 * single-variant single-price product is complete from its product id alone.
 *
 * Returns null when the id resolves to something this picker would never have
 * listed: another store's product, or a retired one on a picker that hides them.
 */
export const resolveIdSelection = ({
    query,
    product,
    storeId,
    includeInactive,
}: ResolveIdSelectionArgs): ResolvedSelection | null => {
    if (!product) return null
    if (product.storeId !== storeId) return null
    if (!includeInactive && !product.active) return null

    let variant: SearchProductOutputItem['variants'][number] | undefined
    let priceId: string | undefined

    if (query.kind === 'price') {
        variant = product.variants.find((v) => v.prices.some((p) => p.id === query.id))
        if (!variant) return null
        priceId = query.id
    } else if (query.kind === 'variant') {
        variant = product.variants.find((v) => v.id === query.id)
        if (!variant) return null
    } else {
        if (product.id !== query.id) return null
        if (product.variants.length === 1) variant = product.variants[0]
    }

    if (!priceId && variant?.prices.length === 1) {
        priceId = variant.prices[0].id
    }

    return { product, variantId: variant?.id, priceId }
}

export interface ResolutionSummary {
    id: string
    /** Steps filled without the user choosing, because only one option existed. */
    collapsedSteps: SelectionStep[]
    /** The step the user still has to pick, null when the selection is complete. */
    remainingStep: Exclude<SelectionStep, 'product'> | null
    remainingCount: number
}

/**
 * Describe what a resolved id did, so the picker can tell the user which steps
 * it skipped for them and which one is still theirs to make.
 */
export const describeResolution = (
    query: ResourceIdQuery,
    selection: ResolvedSelection,
): ResolutionSummary => {
    const { product, variantId, priceId } = selection
    const variant = product.variants.find((v) => v.id === variantId)

    // An id names its own step; anything deeper only filled in because it was
    // the single remaining option.
    const collapsedSteps: SelectionStep[] = []
    if (variantId && query.kind === 'product') collapsedSteps.push('variant')
    if (priceId && query.kind !== 'price') collapsedSteps.push('price')

    if (!variantId) {
        return {
            id: query.id,
            collapsedSteps,
            remainingStep: 'variant',
            remainingCount: product.variants.length,
        }
    }

    if (!priceId) {
        return {
            id: query.id,
            collapsedSteps,
            remainingStep: 'price',
            remainingCount: variant?.prices.length ?? 0,
        }
    }

    return { id: query.id, collapsedSteps, remainingStep: null, remainingCount: 0 }
}
